import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/App';
import { Link } from 'react-router-dom';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDateIST } from '../utils/dateUtils';
import { IndianRupee } from 'lucide-react';

// Consistent brand colors from logo
const COLORS = {
  primary: '#192650',
  accent: '#0DA891',
  background: '#F8FAFC',
  border: '#E2E8F0'
};

// Mobile bottom-sheet override for dialogs — centered on desktop, bottom sheet on phones.
const MOBILE_SHEET = "max-sm:!inset-x-0 max-sm:!top-auto max-sm:!bottom-0 max-sm:!transform-none max-sm:!w-auto max-sm:!max-w-none max-sm:!rounded-b-none max-sm:!rounded-t-2xl max-sm:data-[state=open]:!slide-in-from-bottom max-sm:data-[state=closed]:!slide-out-to-bottom";

const FinanceManagementNew = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [activeTab, setActiveTab] = useState('all');
  const listRef = useRef(null);
  // Tap a summary card → filter the invoice list to that status and scroll to it.
  const goToStatus = (status) => {
    setActiveTab(status);
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };
  
  // Stats
  const [stats, setStats] = useState({
    total_received: 0,
    total_pending: 0,
    total_refunded: 0,
    total_gst: 0,
    total_expenses: 0,
    total_incentives_paid: 0,
    total_commission_paid: 0,
    total_bonus_paid: 0,
    commission_paid_count: 0,
    bonus_paid_count: 0,
    net_balance: 0
  });

  // Filters — default to current month so dashboard stats & invoice list are monthly-scoped
  const _today = new Date();
  const _monthStart = new Date(_today.getFullYear(), _today.getMonth(), 1);
  const _monthEnd = new Date(_today.getFullYear(), _today.getMonth() + 1, 0);
  const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [filters, setFilters] = useState({
    status: '',
    from_date: _fmt(_monthStart),
    to_date: _fmt(_monthEnd),
    search: ''
  });
  // Compact date-range dropdown (replaces the old preset chip row). Defaults to this month.
  const [rangePreset, setRangePreset] = useState('this_month');
  const computeRange = (id) => {
    const t = new Date();
    switch (id) {
      case 'this_month': return { f: _fmt(new Date(t.getFullYear(), t.getMonth(), 1)), to: _fmt(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
      case 'last_month': return { f: _fmt(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: _fmt(new Date(t.getFullYear(), t.getMonth(), 0)) };
      case 'this_year': return { f: _fmt(new Date(t.getFullYear(), 0, 1)), to: _fmt(new Date(t.getFullYear(), 11, 31)) };
      case 'all_time': return { f: '2020-01-01', to: _fmt(new Date(t.getFullYear() + 1, 0, 1)) };
      default: return null;
    }
  };
  const handleRangePreset = (id) => {
    setRangePreset(id);
    const r = computeRange(id);
    if (r) setFilters(prev => ({ ...prev, from_date: r.f, to_date: r.to }));
  };

  // Dialogs
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Form states
  const [receiveForm, setReceiveForm] = useState({
    payment_type: 'full',                    // 'full' | 'partial'
    amount: '',                              // amount for this transaction
    payment_mode: 'bank_transfer',
    payment_date: new Date().toISOString().slice(0, 10),
    transaction_reference: '',
    bank_name: '',
    notes: ''
  });
  const [showTransactionsDialog, setShowTransactionsDialog] = useState(false);

  // Standalone / ad-hoc invoice
  const [showNewInvoiceDialog, setShowNewInvoiceDialog] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [newInvoice, setNewInvoice] = useState({ name: '', phone: '', email: '', address: '', gstin: '', gst_mode: 'exclusive', notes: '' });
  const [invoiceItems, setInvoiceItems] = useState([{ description: '', amount: '' }]);

  // Mobile: collapsible payment cards (tap to expand)
  const [expandedCards, setExpandedCards] = useState({});
  const toggleCard = (id) => setExpandedCards((p) => ({ ...p, [id]: !p[id] }));

  const addInvoiceItem = () => setInvoiceItems((p) => [...p, { description: '', amount: '' }]);
  const removeInvoiceItem = (idx) => setInvoiceItems((p) => p.filter((_, i) => i !== idx));
  const updateInvoiceItem = (idx, field, val) => setInvoiceItems((p) => p.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  const invoiceSubtotal = invoiceItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const resetNewInvoice = () => {
    setNewInvoice({ name: '', phone: '', email: '', address: '', gstin: '', gst_mode: 'exclusive', notes: '' });
    setInvoiceItems([{ description: '', amount: '' }]);
  };
  const handleCreateStandaloneInvoice = async () => {
    if (!newInvoice.name.trim()) { toast.error('Recipient name is required'); return; }
    const items = invoiceItems.filter((it) => it.description.trim() && parseFloat(it.amount) > 0);
    if (items.length === 0) { toast.error('Add at least one line item with description and amount'); return; }
    setCreatingInvoice(true);
    try {
      const res = await apiClient.post('/finance/standalone-invoice', {
        recipient: { name: newInvoice.name, phone: newInvoice.phone, email: newInvoice.email, address: newInvoice.address, gstin: newInvoice.gstin },
        line_items: items.map((it) => ({ description: it.description, amount: parseFloat(it.amount) })),
        gst_mode: newInvoice.gst_mode,
        notes: newInvoice.notes,
      });
      toast.success(`Invoice ${res.data.invoice_number} created`);
      setShowNewInvoiceDialog(false);
      resetNewInvoice();
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };
  const [transactionHistory, setTransactionHistory] = useState({ transactions: [], invoice_total: 0, total_paid: 0, balance_amount: 0 });
  
  const [refundForm, setRefundForm] = useState({
    refund_mode: 'bank_transfer',
    refund_reference: '',
    refund_reason: '',
    refund_amount: ''
  });
  
  const [cancelReason, setCancelReason] = useState('');

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());
      
      if (filters.status) params.append('status', filters.status);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      const response = await apiClient.get(`/finance/payments?${params.toString()}`);
      setPayments(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        total_pages: response.data.pagination?.total_pages || 0
      }));
      
      // Fetch dashboard stats from new endpoint (scoped to current date range)
      fetchDashboardStats();
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchDashboardStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      const qs = params.toString();
      const response = await apiClient.get(`/finance/dashboard-stats-v2${qs ? `?${qs}` : ''}`);
      setStats({
        total_received: response.data.total_received || 0,
        total_pending: response.data.total_pending || 0,
        total_refunded: response.data.total_refunded || 0,
        total_gst: response.data.total_gst || 0,
        total_expenses: response.data.total_expenses || 0,
        total_incentives_paid: response.data.total_incentives_paid || 0,
        total_commission_paid: response.data.total_commission_paid || 0,
        total_bonus_paid: response.data.total_bonus_paid || 0,
        commission_paid_count: response.data.commission_paid_count || 0,
        bonus_paid_count: response.data.bonus_paid_count || 0,
        net_balance: response.data.net_balance || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Fallback to basic calculation
      const received = payments.filter(p => p.status === 'received');
      const pending = payments.filter(p => p.status === 'pending');
      const refunded = payments.filter(p => p.status === 'refunded');
      
      const totalReceived = received.reduce((sum, p) => sum + (p.net_amount || 0), 0);
      const totalPending = pending.reduce((sum, p) => sum + (p.net_amount || 0), 0);
      const totalRefunded = refunded.reduce((sum, p) => sum + (p.refund_amount || 0), 0);
      const totalGst = received.reduce((sum, p) => sum + (p.total_gst || 0), 0);
      
      setStats({
        total_received: totalReceived,
        total_pending: totalPending,
        total_refunded: totalRefunded,
        total_gst: totalGst,
        total_expenses: 0,
        total_incentives_paid: 0,
        net_balance: totalReceived - totalRefunded
      });
    }
  };

  useEffect(() => {
    if (user) {
      fetchPayments();
    }
  }, [user, fetchPayments]);

  // Compute outstanding balance for an invoice (handles both new + legacy)
  const getBalance = (p) => {
    if (!p) return 0;
    if (typeof p.balance_amount === 'number') return p.balance_amount;
    const total = p.net_amount || 0;
    const paid = p.total_paid || 0;
    if (p.status === 'received') return 0;
    if (p.status === 'pending') return total;
    return Math.max(0, total - paid);
  };
  const getTotalPaid = (p) => {
    if (!p) return 0;
    if (typeof p.total_paid === 'number') return p.total_paid;
    if (p.status === 'received') return p.net_amount || 0;
    return 0;
  };

  // Handle receive payment (supports full + partial)
  const handleReceivePayment = async () => {
    if (!selectedPayment) return;
    const balance = getBalance(selectedPayment);
    const isFull = receiveForm.payment_type === 'full';
    const txnAmount = isFull ? balance : parseFloat(receiveForm.amount || 0);

    if (!isFull) {
      if (!txnAmount || txnAmount <= 0) {
        toast.error('Enter a valid amount greater than zero');
        return;
      }
      if (txnAmount > balance + 0.01) {
        toast.error(`Amount cannot exceed outstanding balance ₹${balance.toLocaleString()}`);
        return;
      }
    }

    try {
      const res = await apiClient.post(
        `/finance/payments/${selectedPayment.id}/receive-payment`,
        {
          amount: txnAmount,
          payment_mode: receiveForm.payment_mode,
          payment_date: receiveForm.payment_date ? new Date(receiveForm.payment_date).toISOString() : null,
          transaction_reference: receiveForm.transaction_reference,
          bank_name: receiveForm.bank_name,
          notes: receiveForm.notes,
          is_full_payment: isFull,
        }
      );
      if (res.data?.is_fully_paid) {
        toast.success(`Invoice fully paid · Receipt ${res.data.receipt_number} generated`);
      } else {
        toast.success(`Partial payment ₹${txnAmount.toLocaleString()} recorded · Balance ₹${(res.data?.balance_amount || 0).toLocaleString()}`);
      }
      setShowReceiveDialog(false);
      setSelectedPayment(null);
      setReceiveForm({
        payment_type: 'full', amount: '',
        payment_mode: 'bank_transfer',
        payment_date: new Date().toISOString().slice(0, 10),
        transaction_reference: '', bank_name: '', notes: ''
      });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive payment');
    }
  };

  const openTransactionHistory = async (payment) => {
    setSelectedPayment(payment);
    setShowTransactionsDialog(true);
    try {
      const res = await apiClient.get(`/finance/payments/${payment.id}/transactions`);
      setTransactionHistory(res.data || { transactions: [], invoice_total: 0, total_paid: 0, balance_amount: 0 });
    } catch (e) {
      toast.error('Failed to load transaction history');
      setTransactionHistory({ transactions: [], invoice_total: 0, total_paid: 0, balance_amount: 0 });
    }
  };

  // ─── Payment Reminder (WATI + Razorpay link) ────────────────────────────
  const [sendingReminderId, setSendingReminderId] = useState(null);

  const handleSendReminder = async (payment, force = false) => {
    setSendingReminderId(payment.id);
    try {
      const res = await apiClient.post(
        `/finance/payments/${payment.id}/send-reminder`,
        { force }
      );
      const link = res.data?.payment_link;
      toast.success(
        `Reminder sent to ${res.data?.phone || 'student'} · ₹${(res.data?.balance || 0).toLocaleString()}` +
        (link ? ` · ${res.data.delivery_path === 'session_text' ? 'session text' : 'template'}` : '')
      );
      fetchPayments();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to send reminder';
      // If throttle hit, offer force option
      if (typeof detail === 'string' && detail.toLowerCase().includes('already sent') && !force) {
        if (window.confirm(`${detail}\n\nSend it anyway?`)) {
          await handleSendReminder(payment, true);
          return;
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setSendingReminderId(null);
    }
  };

  // Handle refund payment
  const handleRefundPayment = async () => {
    if (!selectedPayment) return;
    
    if (!refundForm.refund_reason.trim()) {
      toast.error('Please provide a refund reason');
      return;
    }
    
    try {
      await apiClient.put(`/finance/payments/${selectedPayment.id}/refund`, {
        refund_mode: refundForm.refund_mode,
        refund_reference: refundForm.refund_reference,
        refund_reason: refundForm.refund_reason,
        refund_amount: refundForm.refund_amount ? parseFloat(refundForm.refund_amount) : null
      });
      
      toast.success('Payment refunded successfully! Credit note generated.');
      setShowRefundDialog(false);
      setSelectedPayment(null);
      setRefundForm({ refund_mode: 'bank_transfer', refund_reference: '', refund_reason: '', refund_amount: '' });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refund payment');
    }
  };

  // Handle cancel payment
  const handleCancelPayment = async () => {
    if (!selectedPayment) return;
    
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    
    try {
      await apiClient.put(`/finance/payments/${selectedPayment.id}/cancel`, {
        reason: cancelReason
      });
      
      toast.success('Payment cancelled successfully');
      setShowCancelDialog(false);
      setSelectedPayment(null);
      setCancelReason('');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel payment');
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async (paymentId, docType) => {
    try {
      toast.loading(`Generating ${docType === 'credit-note' ? 'credit note' : docType}...`);
      
      const endpoint = `/finance/payments/${paymentId}/${docType}-pdf`;
      const response = await apiClient.get(endpoint, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${docType}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`${docType.charAt(0).toUpperCase() + docType.slice(1)} downloaded successfully`);
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || `Failed to download ${docType}`);
    }
  };

  // Status badge
  const getStatusBadge = (status) => {
    const config = {
      'pending': { bg: 'bg-yellow-100 text-yellow-800', icon: 'fa-clock' },
      'partial': { bg: 'bg-amber-100 text-amber-900 border border-amber-300', icon: 'fa-coins' },
      'received': { bg: 'bg-green-100 text-green-800', icon: 'fa-check-circle' },
      'refunded': { bg: 'bg-red-100 text-red-800', icon: 'fa-undo' },
      'cancelled': { bg: 'bg-gray-100 text-gray-800', icon: 'fa-ban' }
    };
    const c = config[status] || config['pending'];
    const label = status === 'partial' ? 'Partially Paid' : (status?.charAt(0).toUpperCase() + status?.slice(1));
    return (
      <Badge className={`${c.bg} font-medium`}>
        <i className={`fas ${c.icon} mr-1`}></i>
        {label}
      </Badge>
    );
  };

  // GST type badge
  const getGstBadge = (gstType) => {
    const config = {
      'inclusive': { bg: 'bg-blue-100 text-blue-800', label: 'GST Inclusive' },
      'exclusive': { bg: 'bg-purple-100 text-purple-800', label: 'GST Exclusive' },
      'not_applicable': { bg: 'bg-gray-100 text-gray-600', label: 'No GST' }
    };
    const c = config[gstType] || config['inclusive'];
    return <Badge className={`${c.bg} text-xs`}>{c.label}</Badge>;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Filter payments by tab
  const getFilteredPayments = () => {
    if (activeTab === 'all') return payments;
    return payments.filter(p => p.status === activeTab);
  };

  const filteredPayments = getFilteredPayments();

  // Check permissions
  if (user?.role !== 'admin' && user?.role !== 'accountant') {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-lock text-red-600 text-2xl"></i>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-500">You do not have permission to access the finance module.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-4 p-2 sm:p-0 min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* Header - Consistent with other modules */}
      <div className="sticky top-0 z-10 rounded-xl p-3 sm:p-4 shadow-sm" style={{ backgroundColor: COLORS.primary }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: COLORS.accent }}>
              <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-white leading-tight">Finance & Accounts</h1>
              <p className="text-[11px] sm:text-xs text-white/70 hidden sm:block">GST-compliant payment management · Stats scoped to selected date range</p>
            </div>
          </div>
          {/* Action toolbar — Expenses, Incentives & New Invoice */}
          <div className="grid grid-cols-3 gap-1.5 sm:flex sm:flex-none sm:gap-2 sm:justify-end">
            <Link to="/finance/expenses" className="w-full sm:w-auto">
              <Button size="sm" data-testid="finance-expenses-btn"
                      className="h-9 sm:h-11 w-full sm:w-auto text-[11px] sm:text-base font-semibold px-1.5 sm:px-5 justify-center whitespace-nowrap bg-red-500 hover:bg-red-600 text-white shadow-md border-0">
                <i className="fas fa-receipt mr-1 sm:mr-2 text-xs sm:text-base" />
                Expenses
              </Button>
            </Link>
            <Link to="/finance/incentives" className="w-full sm:w-auto">
              <Button size="sm" data-testid="finance-incentives-btn"
                      className="h-9 sm:h-11 w-full sm:w-auto text-[11px] sm:text-base font-semibold px-1.5 sm:px-5 justify-center whitespace-nowrap bg-amber-500 hover:bg-amber-600 text-white shadow-md border-0">
                <i className="fas fa-award mr-1 sm:mr-2 text-xs sm:text-base" />
                Incentives
              </Button>
            </Link>
            <Button
              size="sm"
              data-testid="finance-new-invoice-btn"
              onClick={() => setShowNewInvoiceDialog(true)}
              className="h-9 sm:h-11 w-full sm:w-auto text-[11px] sm:text-base font-semibold px-1.5 sm:px-5 justify-center whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white shadow-md border-0">
              <i className="fas fa-file-invoice mr-1 sm:mr-2 text-xs sm:text-base" />
              <span className="sm:hidden">Invoice</span><span className="hidden sm:inline">New Invoice</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards - 2x3 on Mobile, 6 columns on Desktop - Readable typography */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 sm:gap-3">
        {/* Received */}
        <button type="button" onClick={() => goToStatus('received')} data-testid="stat-card-received" className="text-left bg-green-50 border border-green-200 rounded-lg p-2.5 sm:p-3 min-w-0 overflow-hidden hover:bg-green-100 hover:border-green-300 active:bg-green-200 transition-colors">
          <div className="flex items-center gap-2 sm:flex-row">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="fas fa-check-circle text-white text-xs sm:text-sm" />
            </div>
            <div className="min-w-0 w-full">
              <p className="text-[11px] sm:text-xs text-green-700 font-medium leading-tight flex items-center gap-1">Received <i className="fas fa-chevron-right text-[8px] opacity-60" /></p>
              <p className="text-sm sm:text-base font-bold text-green-900 truncate leading-tight">{formatCurrency(stats.total_received)}</p>
            </div>
          </div>
        </button>
        
        {/* Pending */}
        <button type="button" onClick={() => goToStatus('pending')} data-testid="stat-card-pending" className="text-left bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 sm:p-3 min-w-0 overflow-hidden hover:bg-yellow-100 hover:border-yellow-300 active:bg-yellow-200 transition-colors">
          <div className="flex items-center gap-2 sm:flex-row">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="fas fa-clock text-white text-xs sm:text-sm" />
            </div>
            <div className="min-w-0 w-full">
              <p className="text-[11px] sm:text-xs text-yellow-700 font-medium leading-tight flex items-center gap-1">Pending <i className="fas fa-chevron-right text-[8px] opacity-60" /></p>
              <p className="text-sm sm:text-base font-bold text-yellow-900 truncate leading-tight">{formatCurrency(stats.total_pending)}</p>
            </div>
          </div>
        </button>
        
        {/* Refunded */}
        <button type="button" onClick={() => goToStatus('refunded')} data-testid="stat-card-refunded" className="text-left bg-orange-50 border border-orange-200 rounded-lg p-2.5 sm:p-3 min-w-0 overflow-hidden hover:bg-orange-100 hover:border-orange-300 active:bg-orange-200 transition-colors">
          <div className="flex items-center gap-2 sm:flex-row">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="fas fa-undo text-white text-xs sm:text-sm" />
            </div>
            <div className="min-w-0 w-full">
              <p className="text-[11px] sm:text-xs text-orange-700 font-medium leading-tight flex items-center gap-1">Refunded <i className="fas fa-chevron-right text-[8px] opacity-60" /></p>
              <p className="text-sm sm:text-base font-bold text-orange-900 truncate leading-tight">{formatCurrency(stats.total_refunded)}</p>
            </div>
          </div>
        </button>
        
        {/* Expenses */}
        <Link to="/finance/expenses" className="bg-red-50 border border-red-200 rounded-lg p-2.5 sm:p-3 block min-w-0 overflow-hidden hover:bg-red-100 hover:border-red-300 active:bg-red-200 transition-colors">
          <div className="flex items-center gap-2 sm:flex-row">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="fas fa-receipt text-white text-xs sm:text-sm" />
            </div>
            <div className="min-w-0 w-full">
              <p className="text-[11px] sm:text-xs text-red-700 font-medium leading-tight flex items-center gap-1">
                Expenses <i className="fas fa-arrow-right text-[9px] opacity-70" />
              </p>
              <p className="text-sm sm:text-base font-bold text-red-900 truncate leading-tight">{formatCurrency(stats.total_expenses)}</p>
            </div>
          </div>
        </Link>
        
        {/* GST */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 sm:flex-row">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="fas fa-percent text-white text-xs sm:text-sm" />
            </div>
            <div className="min-w-0 w-full">
              <p className="text-[11px] sm:text-xs text-blue-700 font-medium leading-tight">GST</p>
              <p className="text-sm sm:text-base font-bold text-blue-900 truncate leading-tight">{formatCurrency(stats.total_gst)}</p>
            </div>
          </div>
        </div>
        
        {/* Net Balance */}
        <div className={`border-2 rounded-lg p-2.5 sm:p-3 min-w-0 overflow-hidden ${stats.net_balance >= 0 ? 'bg-teal-50 border-teal-300' : 'bg-rose-50 border-rose-300'}`}>
          <div className="flex items-center gap-2 sm:flex-row">
            <div className={`w-8 h-8 sm:w-9 sm:h-9 ${stats.net_balance >= 0 ? 'bg-teal-500' : 'bg-rose-500'} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <i className={`fas ${stats.net_balance >= 0 ? 'fa-chart-line' : 'fa-exclamation-triangle'} text-white text-xs sm:text-sm`} />
            </div>
            <div className="min-w-0 w-full">
              <p className={`text-[11px] sm:text-xs font-medium leading-tight ${stats.net_balance >= 0 ? 'text-teal-700' : 'text-rose-700'}`}>Net Balance</p>
              <p className={`text-sm sm:text-base font-bold truncate leading-tight ${stats.net_balance >= 0 ? 'text-teal-900' : 'text-rose-900'}`}>{formatCurrency(stats.net_balance)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters - Clean, mobile-first */}
      <div className="bg-white rounded-lg border p-2.5 sm:p-3 space-y-2">
        {/* Search student */}
        <Input
          placeholder="Search student..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className="h-10 text-sm"
          data-testid="finance-search-input"
        />
        {/* Range dropdown + From/To */}
        <div className="grid grid-cols-2 sm:grid-cols-[minmax(0,10rem)_1fr_1fr] gap-2 items-end">
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-[10px] text-gray-500 block mb-0.5">Range</Label>
            <Select value={rangePreset} onValueChange={handleRangePreset}>
              <SelectTrigger className="h-10 text-sm" data-testid="finance-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_month" data-testid="finance-range-this_month">This Month</SelectItem>
                <SelectItem value="last_month" data-testid="finance-range-last_month">Last Month</SelectItem>
                <SelectItem value="this_year" data-testid="finance-range-this_year">This Year</SelectItem>
                <SelectItem value="all_time" data-testid="finance-range-all_time">All Time</SelectItem>
                <SelectItem value="custom" data-testid="finance-range-custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-gray-500 block mb-0.5">From</Label>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => { setRangePreset('custom'); setFilters(prev => ({ ...prev, from_date: e.target.value })); }}
              className="h-10 text-sm"
              data-testid="finance-from-date"
            />
          </div>
          <div>
            <Label className="text-[10px] text-gray-500 block mb-0.5">To</Label>
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => { setRangePreset('custom'); setFilters(prev => ({ ...prev, to_date: e.target.value })); }}
              className="h-10 text-sm"
              data-testid="finance-to-date"
            />
          </div>
        </div>
        {/* Download statement */}
        <button
          type="button"
          onClick={async () => {
            try {
              const params = new URLSearchParams();
              if (filters.from_date) params.append('from_date', filters.from_date);
              if (filters.to_date) params.append('to_date', filters.to_date);
              const response = await apiClient.get(`/finance/monthly-statement-pdf?${params.toString()}`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `financial-statement-${filters.from_date}-to-${filters.to_date}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              toast.success('Statement downloaded');
            } catch (e) {
              toast.error('Failed to download statement');
            }
          }}
          className="w-full inline-flex items-center justify-center gap-2 h-10 px-3 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition shadow-sm"
          data-testid="download-monthly-statement-btn"
          title="Download a P&L-style PDF statement for the selected date range"
        >
          <i className="fas fa-file-pdf"></i>
          <span>Download Statement</span>
        </button>
      </div>

      {/* Tabs */}
      <div ref={listRef} className="scroll-mt-16" />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto p-1 gap-1">
          <TabsTrigger value="all" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
            All ({payments.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
            <i className="fas fa-clock mr-1 text-yellow-500"></i>
            <span className="hidden sm:inline">Pending</span> ({payments.filter(p => p.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="partial" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
            <i className="fas fa-coins mr-1 text-amber-600"></i>
            <span className="hidden sm:inline">Partial</span> ({payments.filter(p => p.status === 'partial').length})
          </TabsTrigger>
          <TabsTrigger value="received" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
            <i className="fas fa-check-circle mr-1 text-green-500"></i>
            <span className="hidden sm:inline">Received</span> ({payments.filter(p => p.status === 'received').length})
          </TabsTrigger>
          <TabsTrigger value="refunded" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
            <i className="fas fa-undo mr-1 text-red-500"></i>
            <span className="hidden sm:inline">Refunded</span> ({payments.filter(p => p.status === 'refunded').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="fas fa-spinner fa-spin text-2xl text-teal-600"></i>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-receipt text-4xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">No payments found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPayments.map((payment) => (
                <Card key={payment.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Mobile summary bar — tap to expand */}
                      <button
                        type="button"
                        onClick={() => toggleCard(payment.id)}
                        data-testid={`payment-card-toggle-${payment.id}`}
                        className="lg:hidden w-full flex items-center justify-between gap-3 text-left"
                      >
                        <div className="min-w-0 flex items-center gap-2.5">
                          <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
                            <i className="fas fa-user text-teal-600 text-sm" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 text-sm truncate">{payment.student_name || 'Unknown Student'}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              {getStatusBadge(payment.status)}
                              <span className="text-[11px] text-gray-400 truncate">{payment.payment_type_name}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-base font-bold text-gray-900">{formatCurrency(payment.net_amount)}</span>
                          <i className={`fas fa-chevron-${expandedCards[payment.id] ? 'up' : 'down'} text-gray-400 text-xs`} />
                        </div>
                      </button>

                      {/* Left - Student & Payment Info */}
                      <div className={`flex-1 ${expandedCards[payment.id] ? '' : 'hidden'} lg:block`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-user text-teal-600"></i>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{payment.student_name || 'Unknown Student'}</h3>
                            <p className="text-sm text-gray-500">{payment.payment_type_name}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {getStatusBadge(payment.status)}
                              {getGstBadge(payment.gst_type)}
                              {payment.invoice_number && (
                                <Badge variant="outline" className="text-xs">
                                  <i className="fas fa-file-invoice mr-1"></i>
                                  {payment.invoice_number}
                                </Badge>
                              )}
                              {payment.receipt_number && (
                                <Badge variant="outline" className="text-xs bg-green-50">
                                  <i className="fas fa-receipt mr-1"></i>
                                  {payment.receipt_number}
                                </Badge>
                              )}
                              {payment.auto_reconciled && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                                  data-testid={`auto-reconciled-badge-${payment.id}`}
                                  title={
                                    `Auto-reconciled via Razorpay`
                                    + (payment.razorpay_payment_id ? ` · ${payment.razorpay_payment_id}` : '')
                                    + (payment.auto_reconciled_at ? ` · ${formatDateIST(payment.auto_reconciled_at)}` : '')
                                  }
                                >
                                  <i className="fas fa-bolt mr-1"></i>
                                  Auto-reconciled · Razorpay
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Middle - Amount & GST + Partial Payment Breakdown */}
                      <div className={`lg:text-right ${expandedCards[payment.id] ? '' : 'hidden'} lg:block`}>
                        <p className="text-2xl font-bold text-gray-900" data-testid={`invoice-total-${payment.id}`}>{formatCurrency(payment.net_amount)}</p>
                        {payment.gst_type !== 'not_applicable' && payment.total_gst > 0 && (
                          <p className="text-xs text-gray-500">
                            Base: {formatCurrency(payment.base_amount)} | 
                            CGST: {formatCurrency(payment.cgst_amount)} | 
                            SGST: {formatCurrency(payment.sgst_amount)}
                          </p>
                        )}
                        {(payment.status === 'partial' || (payment.status === 'received' && (payment.payment_transactions || []).length > 1)) && (
                          <div className="mt-2 inline-flex flex-col text-xs lg:items-end gap-0.5">
                            <span className="text-gray-700">
                              Paid: <b className="text-green-700" data-testid={`invoice-paid-${payment.id}`}>{formatCurrency(getTotalPaid(payment))}</b>
                            </span>
                            {payment.status === 'partial' && (
                              <span className="text-amber-700">
                                Balance: <b data-testid={`invoice-balance-${payment.id}`}>{formatCurrency(getBalance(payment))}</b>
                              </span>
                            )}
                            <button
                              type="button"
                              className="text-[11px] text-blue-600 hover:underline"
                              onClick={() => openTransactionHistory(payment)}
                              data-testid={`view-transactions-btn-${payment.id}`}
                            >
                              <i className="fas fa-history mr-1" />View {(payment.payment_transactions || []).length} transaction{((payment.payment_transactions || []).length === 1 ? '' : 's')}
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDateIST(payment.created_at)}
                        </p>
                      </div>

                      {/* Right - Actions */}
                      <div className={`${expandedCards[payment.id] ? 'grid' : 'hidden'} grid-cols-2 gap-2 lg:flex lg:flex-col [&>*]:w-full lg:[&>*]:w-auto mt-1 pt-3 border-t border-gray-100 lg:mt-0 lg:pt-0 lg:border-t-0`}>
                        {(payment.status === 'pending' || payment.status === 'partial') && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                setSelectedPayment(payment);
                                setReceiveForm(prev => ({
                                  ...prev,
                                  payment_type: 'full',
                                  amount: '',
                                  payment_date: new Date().toISOString().slice(0, 10),
                                }));
                                setShowReceiveDialog(true);
                              }}
                              data-testid={`receive-btn-${payment.id}`}
                            >
                              <i className="fas fa-check mr-1"></i>
                              {payment.status === 'partial' ? 'Receive Payment' : 'Receive'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                setSelectedPayment(payment);
                                setShowCancelDialog(true);
                              }}
                            >
                              <i className="fas fa-ban mr-1"></i>
                              Cancel
                            </Button>
                          </>
                        )}
                        
                        {payment.status === 'received' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              setSelectedPayment(payment);
                              setRefundForm(prev => ({ ...prev, refund_amount: payment.net_amount.toString() }));
                              setShowRefundDialog(true);
                            }}
                          >
                            <i className="fas fa-undo mr-1"></i>
                            Refund
                          </Button>
                        )}
                        
                        {/* PDF Download Buttons */}
                        {payment.invoice_number && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => handleDownloadPdf(payment.id, 'invoice')}
                          >
                            <i className="fas fa-file-invoice mr-1"></i>
                            Invoice
                          </Button>
                        )}

                        {/* Send WhatsApp Reminder — pending or partial only */}
                        {(payment.status === 'pending' || payment.status === 'partial') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            disabled={sendingReminderId === payment.id}
                            onClick={() => handleSendReminder(payment)}
                            data-testid={`send-reminder-btn-${payment.id}`}
                            title={
                              payment.last_reminder_sent_at
                                ? `Last sent ${formatDateIST(payment.last_reminder_sent_at)} · ${payment.reminder_count || 0} total`
                                : 'Send WhatsApp reminder with Razorpay payment link for the outstanding balance'
                            }
                          >
                            {sendingReminderId === payment.id ? (
                              <i className="fas fa-spinner fa-spin mr-1" />
                            ) : (
                              <i className="fab fa-whatsapp mr-1" />
                            )}
                            {payment.reminder_count > 0 ? `Remind (${payment.reminder_count})` : 'Send Reminder'}
                          </Button>
                        )}
                        
                        {payment.status === 'received' && payment.receipt_number && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => handleDownloadPdf(payment.id, 'receipt')}
                          >
                            <i className="fas fa-receipt mr-1"></i>
                            Receipt
                          </Button>
                        )}
                        
                        {payment.status === 'refunded' && payment.credit_note_number && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDownloadPdf(payment.id, 'credit-note')}
                          >
                            <i className="fas fa-file-alt mr-1"></i>
                            Credit Note
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowDetailsDialog(true);
                          }}
                        >
                          <i className="fas fa-eye mr-1"></i>
                          Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            <i className="fas fa-chevron-left"></i>
          </Button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page === pagination.total_pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            <i className="fas fa-chevron-right"></i>
          </Button>
        </div>
      )}

      {/* New Standalone Invoice Dialog */}
      <Dialog open={showNewInvoiceDialog} onOpenChange={(o) => { setShowNewInvoiceDialog(o); if (!o) resetNewInvoice(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto max-sm:!inset-x-0 max-sm:!top-auto max-sm:!bottom-0 max-sm:!transform-none max-sm:!w-auto max-sm:!max-w-none max-sm:!h-[92vh] max-sm:!max-h-[92vh] max-sm:!rounded-b-none max-sm:!rounded-t-2xl max-sm:data-[state=open]:!slide-in-from-bottom max-sm:data-[state=closed]:!slide-out-to-bottom" data-testid="new-invoice-dialog">
          <DialogHeader>
            <DialogTitle>New Standalone Invoice</DialogTitle>
            <DialogDescription>Raise an ad-hoc invoice (separate MISC-series number) for a recipient not in the student pipeline.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Recipient Name *</Label>
                <Input data-testid="invoice-recipient-name" value={newInvoice.name} onChange={(e) => setNewInvoice({ ...newInvoice, name: e.target.value })} placeholder="Company / Person name" />
              </div>
              <div>
                <Label>GSTIN (optional)</Label>
                <Input data-testid="invoice-recipient-gstin" value={newInvoice.gstin} onChange={(e) => setNewInvoice({ ...newInvoice, gstin: e.target.value })} placeholder="e.g. 29ABCDE1234F1Z5" />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input data-testid="invoice-recipient-phone" value={newInvoice.phone} onChange={(e) => setNewInvoice({ ...newInvoice, phone: e.target.value })} />
              </div>
              <div>
                <Label>Email (optional)</Label>
                <Input data-testid="invoice-recipient-email" value={newInvoice.email} onChange={(e) => setNewInvoice({ ...newInvoice, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Address (optional)</Label>
              <Textarea data-testid="invoice-recipient-address" value={newInvoice.address} onChange={(e) => setNewInvoice({ ...newInvoice, address: e.target.value })} rows={2} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addInvoiceItem} data-testid="invoice-add-item-btn"><i className="fas fa-plus mr-1" />Add item</Button>
              </div>
              <div className="space-y-2">
                {invoiceItems.map((it, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input className="flex-1 min-w-0" placeholder="Item description" value={it.description} onChange={(e) => updateInvoiceItem(idx, 'description', e.target.value)} data-testid={`invoice-item-desc-${idx}`} />
                    <Input className="!w-24 shrink-0" type="number" placeholder="Amount" value={it.amount} onChange={(e) => updateInvoiceItem(idx, 'amount', e.target.value)} data-testid={`invoice-item-amount-${idx}`} />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeInvoiceItem(idx)} disabled={invoiceItems.length === 1} data-testid={`invoice-item-remove-${idx}`} className="text-red-500"><i className="fas fa-trash" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>GST</Label>
                <Select value={newInvoice.gst_mode} onValueChange={(v) => setNewInvoice({ ...newInvoice, gst_mode: v })}>
                  <SelectTrigger data-testid="invoice-gst-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exclusive">18% GST — added on top (Exclusive)</SelectItem>
                    <SelectItem value="inclusive">18% GST — included in amount (Inclusive)</SelectItem>
                    <SelectItem value="none">No GST (0%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <div className="text-sm text-slate-600" data-testid="invoice-subtotal">Subtotal: <span className="font-semibold">{formatCurrency(invoiceSubtotal)}</span>{newInvoice.gst_mode === 'exclusive' ? ' + GST' : ''}</div>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={newInvoice.notes} onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })} rows={2} data-testid="invoice-notes" />
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t mt-2 gap-2">
            <Button variant="outline" onClick={() => { setShowNewInvoiceDialog(false); resetNewInvoice(); }}>Cancel</Button>
            <Button onClick={handleCreateStandaloneInvoice} disabled={creatingInvoice} data-testid="invoice-submit-btn" style={{ backgroundColor: COLORS.accent }} className="text-white">
              {creatingInvoice ? 'Creating…' : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Payment Dialog — Full or Partial */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto max-sm:!h-[92vh] max-sm:!max-h-[92vh] ${MOBILE_SHEET}`}>
          <DialogHeader>
            <DialogTitle>Receive Payment</DialogTitle>
            <DialogDescription>
              Record a payment for {selectedPayment?.student_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Invoice Total</span>
                <span className="font-bold text-gray-900">{formatCurrency(selectedPayment?.net_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-green-700">Already Paid</span>
                <span className="font-semibold text-green-800">{formatCurrency(getTotalPaid(selectedPayment))}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-green-200 pt-1 mt-1">
                <span className="text-amber-800 font-medium">Outstanding Balance</span>
                <span className="font-bold text-amber-900" data-testid="dialog-balance">{formatCurrency(getBalance(selectedPayment))}</span>
              </div>
              {selectedPayment?.gst_type !== 'not_applicable' && selectedPayment?.total_gst > 0 && (
                <p className="text-[11px] text-gray-500 pt-1 border-t border-green-200 mt-1">
                  GST {formatCurrency(selectedPayment?.total_gst)} is recognized when the invoice is fully closed.
                </p>
              )}
            </div>

            {/* Payment Type — Full vs Partial */}
            <div className="space-y-2">
              <Label>Payment Type *</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition ${receiveForm.payment_type === 'full' ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 hover:border-gray-300'}`}
                  onClick={() => setReceiveForm(prev => ({ ...prev, payment_type: 'full', amount: '' }))}
                  data-testid="payment-type-full"
                >
                  <i className="fas fa-check-circle mr-1" />Full Payment
                  <p className="text-[11px] text-gray-500 mt-0.5">Pay the entire balance now</p>
                </button>
                <button
                  type="button"
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition ${receiveForm.payment_type === 'partial' ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-200 hover:border-gray-300'}`}
                  onClick={() => setReceiveForm(prev => ({ ...prev, payment_type: 'partial' }))}
                  data-testid="payment-type-partial"
                >
                  <i className="fas fa-coins mr-1" />Partial Payment
                  <p className="text-[11px] text-gray-500 mt-0.5">Receive a part of the amount</p>
                </button>
              </div>
            </div>

            {receiveForm.payment_type === 'partial' && (
              <div className="space-y-2">
                <Label>Amount Received *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={`Max ₹${getBalance(selectedPayment).toLocaleString()}`}
                  value={receiveForm.amount}
                  onChange={(e) => setReceiveForm(prev => ({ ...prev, amount: e.target.value }))}
                  data-testid="partial-amount-input"
                />
                <p className="text-[11px] text-gray-500">
                  Enter any amount up to the outstanding balance.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={receiveForm.payment_date}
                  onChange={(e) => setReceiveForm(prev => ({ ...prev, payment_date: e.target.value }))}
                  data-testid="payment-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <Select
                  value={receiveForm.payment_mode}
                  onValueChange={(v) => setReceiveForm(prev => ({ ...prev, payment_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Transaction Reference</Label>
              <Input
                placeholder="Transaction ID / UTR / Cheque No."
                value={receiveForm.transaction_reference}
                onChange={(e) => setReceiveForm(prev => ({ ...prev, transaction_reference: e.target.value }))}
              />
            </div>
            
            {receiveForm.payment_mode === 'bank_transfer' && (
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input
                  placeholder="Bank name"
                  value={receiveForm.bank_name}
                  onChange={(e) => setReceiveForm(prev => ({ ...prev, bank_name: e.target.value }))}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={receiveForm.notes}
                onChange={(e) => setReceiveForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleReceivePayment} data-testid="confirm-receive-btn">
              <i className="fas fa-check mr-2"></i>
              {receiveForm.payment_type === 'full' ? 'Confirm Full Payment' : 'Record Partial Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={showTransactionsDialog} onOpenChange={setShowTransactionsDialog}> <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${MOBILE_SHEET}`}>
          <DialogHeader>
            <DialogTitle>Payment Transactions</DialogTitle>
            <DialogDescription>
              {selectedPayment?.invoice_number} · {selectedPayment?.student_name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <p className="text-[11px] text-gray-500">Invoice Total</p>
              <p className="text-sm font-bold">{formatCurrency(transactionHistory.invoice_total)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-2 text-center">
              <p className="text-[11px] text-green-600">Total Paid</p>
              <p className="text-sm font-bold text-green-800">{formatCurrency(transactionHistory.total_paid)}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-2 text-center">
              <p className="text-[11px] text-amber-700">Balance</p>
              <p className="text-sm font-bold text-amber-900">{formatCurrency(transactionHistory.balance_amount)}</p>
            </div>
          </div>
          <div className="border rounded-lg max-h-[60vh] overflow-y-auto">
            {(transactionHistory.transactions || []).length === 0 ? (
              <p className="p-4 text-center text-sm text-gray-500">No transactions recorded yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Mode</th>
                    <th className="text-left p-2">Reference</th>
                    <th className="text-left p-2">By</th>
                  </tr>
                </thead>
                <tbody>
                  {(transactionHistory.transactions || []).map((t, i) => {
                    const isRazorpay = (t.recorded_by_name === 'Razorpay (Auto)')
                      || (t.recorded_by === 'razorpay_webhook')
                      || (typeof t.transaction_reference === 'string' && t.transaction_reference.startsWith('pay_'));
                    return (
                    <tr key={t.id || i} className="border-t" data-testid={`txn-row-${i}`}>
                      <td className="p-2">{formatDateIST(t.payment_date)}</td>
                      <td className="text-right p-2 font-semibold text-green-700">{formatCurrency(t.amount)}</td>
                      <td className="p-2 capitalize">
                        {(t.payment_mode || '').replace(/_/g, ' ')}
                        {isRazorpay && (
                          <span
                            className="ml-1 inline-flex items-center rounded bg-blue-50 text-blue-700 border border-blue-200 px-1 text-[10px] align-middle"
                            data-testid={`txn-razorpay-tag-${i}`}
                            title="Auto-reconciled via Razorpay webhook"
                          >
                            <i className="fas fa-bolt mr-0.5"></i>Razorpay
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-gray-600">{t.transaction_reference || '—'}</td>
                      <td className="p-2 text-xs text-gray-500">{t.recorded_by_name || '—'}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransactionsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Payment Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto ${MOBILE_SHEET}`}>
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
            <DialogDescription>
              Refund payment for {selectedPayment?.student_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Original Amount:</strong> {formatCurrency(selectedPayment?.net_amount)}
              </p>
              {selectedPayment?.receipt_number && (
                <p className="text-xs text-red-700 mt-1">
                  Receipt: {selectedPayment.receipt_number}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Refund Amount *</Label>
              <Input
                type="number"
                placeholder="Full refund if empty"
                value={refundForm.refund_amount}
                onChange={(e) => setRefundForm(prev => ({ ...prev, refund_amount: e.target.value }))}
              />
              <p className="text-xs text-gray-500">Leave empty for full refund</p>
            </div>
            
            <div className="space-y-2">
              <Label>Refund Mode *</Label>
              <Select
                value={refundForm.refund_mode}
                onValueChange={(v) => setRefundForm(prev => ({ ...prev, refund_mode: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Refund Reference</Label>
              <Input
                placeholder="Refund transaction reference"
                value={refundForm.refund_reference}
                onChange={(e) => setRefundForm(prev => ({ ...prev, refund_reference: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Refund *</Label>
              <Textarea
                placeholder="Why is this payment being refunded?"
                value={refundForm.refund_reason}
                onChange={(e) => setRefundForm(prev => ({ ...prev, refund_reason: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleRefundPayment}>
              <i className="fas fa-undo mr-2"></i>
              Process Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Payment Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto ${MOBILE_SHEET}`}>
          <DialogHeader>
            <DialogTitle>Cancel Payment</DialogTitle>
            <DialogDescription>
              Cancel pending payment for {selectedPayment?.student_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-800">
                <strong>Amount:</strong> {formatCurrency(selectedPayment?.net_amount)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                This payment will be marked as cancelled and will have no revenue impact.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Cancellation *</Label>
              <Textarea
                placeholder="Why is this payment being cancelled?"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Go Back</Button>
            <Button variant="destructive" onClick={handleCancelPayment}>
              <i className="fas fa-ban mr-2"></i>
              Cancel Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${MOBILE_SHEET}`}>
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4 py-4">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${
                selectedPayment.status === 'received' ? 'bg-green-50' :
                selectedPayment.status === 'pending' ? 'bg-yellow-50' :
                selectedPayment.status === 'refunded' ? 'bg-red-50' : 'bg-gray-50'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(selectedPayment.net_amount)}</p>
                    <p className="text-sm opacity-75">{selectedPayment.payment_type_name}</p>
                  </div>
                  {getStatusBadge(selectedPayment.status)}
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Student</Label>
                  <p className="font-medium">{selectedPayment.student_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Counsellor</Label>
                  <p className="font-medium">{selectedPayment.counsellor_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Invoice Number</Label>
                  <p className="font-medium">{selectedPayment.invoice_number || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Receipt Number</Label>
                  <p className="font-medium">{selectedPayment.receipt_number || 'N/A'}</p>
                </div>
              </div>
              
              {/* GST Details */}
              {selectedPayment.gst_type !== 'not_applicable' && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">GST Breakup ({selectedPayment.gst_type})</p>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-blue-600">Base Amount</p>
                      <p className="font-medium">{formatCurrency(selectedPayment.base_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">CGST (9%)</p>
                      <p className="font-medium">{formatCurrency(selectedPayment.cgst_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">SGST (9%)</p>
                      <p className="font-medium">{formatCurrency(selectedPayment.sgst_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Total GST</p>
                      <p className="font-medium">{formatCurrency(selectedPayment.total_gst)}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Received Details */}
              {selectedPayment.status === 'received' && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-2">Payment Received</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-green-600">Payment Mode</p>
                      <p className="font-medium">{selectedPayment.payment_mode?.replace('_', ' ').toUpperCase() || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600">Transaction Reference</p>
                      <p className="font-medium">{selectedPayment.transaction_reference || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600">Received Date</p>
                      <p className="font-medium">{formatDateIST(selectedPayment.received_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600">Received By</p>
                      <p className="font-medium">{selectedPayment.received_by_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Refund Details */}
              {selectedPayment.status === 'refunded' && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">Refund Details</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-red-600">Refund Amount</p>
                      <p className="font-medium">{formatCurrency(selectedPayment.refund_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-red-600">Refund Mode</p>
                      <p className="font-medium">{selectedPayment.refund_mode?.replace('_', ' ').toUpperCase() || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-red-600">Refund Date</p>
                      <p className="font-medium">{formatDateIST(selectedPayment.refund_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-red-600">Processed By</p>
                      <p className="font-medium">{selectedPayment.refund_processed_by_name || 'N/A'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-red-600">Reason</p>
                      <p className="font-medium">{selectedPayment.refund_reason || 'N/A'}</p>
                    </div>
                    {selectedPayment.credit_note_number && (
                      <div className="col-span-2">
                        <p className="text-xs text-red-600">Credit Note</p>
                        <p className="font-medium">{selectedPayment.credit_note_number}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {selectedPayment.notes && (
                <div>
                  <Label className="text-xs text-gray-500">Notes</Label>
                  <p className="text-sm text-gray-700">{selectedPayment.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceManagementNew;
